/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { showDebug } from '../../helpers/debug/debug';
import Localizer from '../../helpers/localization';
import { BUTTON_HOVER_COLORS } from '../../helpers/ui/constants';
import { Theme, type ThemeType } from '../../helpers/ui/theme';
import InfiniteCircleLoader from '../loader/infiniteCircleLoader';
import ProgressBar from '../progressBar/ProgressBar';
import './ButtonFX.css';
import { ButtonKind, HeightKind, WidthKind } from './types';

type PropType = {
  +allowZeroProgress?: boolean,
  +'aria-hidden'?: boolean,
  +'aria-label'?: string,
  +'aria-labelledby'?: string,
  +autoFocus?: boolean,
  +children: React.Node,
  +className?: string,
  +data?: any,
  +hasPadding?: boolean,
  +heightKind?: HeightKind,
  +isDisabled?: boolean,
  +isLoading?: boolean,
  +kind?: ButtonKind,
  +onClick?: (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>, data: any) => Promise<void> | void,
  +progress?: number | null,
  +theme?: ThemeType,
  +widthKind?: WidthKind,
};

const ButtonFX = (buttonFXProps: PropType): React.Node => {
  const {
    allowZeroProgress = true,
    'aria-hidden': ariaHidden,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    autoFocus = false,
    children,
    className,
    data,
    hasPadding = false,
    heightKind = HeightKind.Large,
    isDisabled = false,
    isLoading = false,
    kind = ButtonKind.Regular,
    onClick,
    progress,
    theme = Theme.Dark,
    widthKind = WidthKind.Large,
  } = buttonFXProps;

  const isDebugModeEnabled = useSelector<boolean>((state) => state.appConfiguration.isDebugModeEnabled);
  const [isButtonDown, setIsButtonDown] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [mousePosition, setMousePosition] = useState<{ x: number, y: number }>({ x: 0, y: 0 });

  const hoverLayerElt = useRef<HTMLElement | null>(null);
  const buttonRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (autoFocus) {
      buttonRef.current?.focus();
    }
  }, [autoFocus]);

  const showDebugInfo = useCallback(() => {
    showDebug('ButtonFX', {
      props: { allowZeroProgress, children, className, data, hasPadding, heightKind, isDisabled, isLoading, kind, onClick: onClick ? '<defined>' : '<undefined>', progress, theme, widthKind },
      propsFields: ['allowZeroProgress', 'children', 'className', 'data', 'hasPadding', 'heightKind', 'isDisabled', 'isLoading', 'kind', 'onClick', 'progress'],
      state: { isButtonDown, isHovered, mousePosition },
      stateFields: ['isButtonDown', 'isHovered', 'mousePosition'],
    });
  }, [allowZeroProgress, children, className, data, hasPadding, heightKind, isButtonDown, isDisabled, isHovered, isLoading, kind, mousePosition, onClick, progress, theme, widthKind]);

  const handleButtonOnClick = useCallback(
    (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      const { altKey, ctrlKey } = event;

      if (isDebugModeEnabled && (ctrlKey || altKey)) {
        event.preventDefault();
        event.stopPropagation();

        showDebugInfo();
        return;
      }

      if (isDisabled || isLoading || !onClick) {
        return;
      }

      onClick(event, data);
    },
    [data, isDebugModeEnabled, isDisabled, isLoading, onClick, showDebugInfo],
  );

  const handleMouseDown = useCallback(() => {
    setIsButtonDown(true);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsButtonDown(false);
  }, []);

  const handleMouseEnter = useCallback((event: SyntheticMouseEvent<HTMLElement>) => {
    setIsButtonDown(event.buttons === 1);
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleMouseMove = useCallback((event: SyntheticMouseEvent<HTMLElement>) => {
    if (!hoverLayerElt.current) {
      return;
    }

    const { clientX, clientY } = event;
    const { left, top } = hoverLayerElt.current.getBoundingClientRect();

    setMousePosition({ x: clientX - left, y: clientY - top });
  }, []);

  const background = useMemo(() => {
    if (isHovered) {
      if (isButtonDown) {
        return BUTTON_HOVER_COLORS.Cursor;
      }

      const { x, y } = mousePosition;
      return `radial-gradient(circle at ${x}px ${y}px, ${BUTTON_HOVER_COLORS.Cursor} 0%, ${BUTTON_HOVER_COLORS.Main} 100%)`;
    }

    return 'none';
  }, [isButtonDown, isHovered, kind, mousePosition]);

  const loaderElt = useMemo(
    () =>
      isLoading ? (
        <div aria-label={Localizer.localize('accessibility.common.processing')} aria-live='polite' className='loader' role='status'>
          <InfiniteCircleLoader />
        </div>
      ) : null,
    [isLoading],
  );

  const disabledLayerElt = useMemo(() => (isDisabled || isLoading ? <div className='disabled' /> : null), [isDisabled, isLoading]);

  return (
    <button
      aria-busy={isLoading}
      aria-disabled={isDisabled || isLoading}
      aria-hidden={ariaHidden}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      className={clsx('buttonFX', className, theme, widthKind !== WidthKind.Content && (widthKind as string), heightKind as string, kind === ButtonKind.Empty && 'empty', isLoading && 'loading')}
      disabled={isDisabled}
      onClick={handleButtonOnClick}
      ref={buttonRef}
      type='button'
    >
      <div
        className='hoverLayer'
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        ref={hoverLayerElt}
        style={{ background }}
      />
      <ProgressBar allowZeroProgress={allowZeroProgress} docked progress={progress} />
      <div className={clsx('content', hasPadding && 'padding')}>{children}</div>
      {disabledLayerElt}
      {loaderElt}
    </button>
  );
};

export default ButtonFX;
